import { type CellContext, type ColumnDef } from '@tanstack/react-table'
import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { UserActionDialog } from '@/components/UserActionDialog'
import { MoreHorizontal, Edit, Trash2, ArrowUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { showSuccessToast } from '../ui/toast'
import AddCategoryDrawer from './AddCategoryDrawer'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

export interface Category {
  id: string
  name: string
  description: string
  events: number
  createdAt: string
  status: 'active' | 'inactive'
}

interface CategoryColumnsOptions {
  onEdit?: (category: Category) => void
  onDelete?: (category: Category) => void
  onViewEvents?: (category: Category) => void
}

const getStatusBadgeProps = (status: string) => {
  switch (status) {
    case 'active':
      return {
        className:
          'bg-ss-positive-50 text-ss-positive-200 border-ss-positive-100',
        children: 'Active',
      }
    case 'inactive':
      return {
        className:
          'bg-ss-action-gray-50 text-ss-neutral-600 border-ss-neutral-200',
        children: 'Inactive',
      }
    default:
      return {
        className:
          'bg-ss-action-gray-50 text-ss-neutral-600 border-ss-neutral-200',
        children: status,
      }
  }
}

const getCell = (accessorKey: string, options?: CategoryColumnsOptions) => {
  return ({ row, getValue }: CellContext<Category, unknown>) => {
    const category = row.original
    const [dialogOpen, setDialogOpen] = useState(false)
    const [isEditCategoryOpen, setIsEditCategoryOpen] = useState(false)
    const hasEvents = category.events > 0
    const handleCategorySubmit = () => {
      showSuccessToast(
        'Category updated.',
        'The category has been successfully removed from the system.'
      )
    }

    const handleDeleteConfirm = async () => {
      if (!hasEvents) {
        options?.onDelete?.(category)
      }
      setDialogOpen(false)
    }

    switch (accessorKey) {
      case 'name':
        return (
          <div className="text-sm font-semibold text-ss-neutral-950 leading-4.5 ">
            {getValue() as string}
          </div>
        )

      case 'description':
        return (
          <div className="text-sm font-medium text-ss-neutral-600 leading-4.5  max-w-[500px] truncate">
            {getValue() as string}
          </div>
        )

      case 'eventsUsing':
        return (
          <button
            className="text-sm font-medium text-ss-blue-500 leading-4.5  underline hover:no-underline"
            onClick={() => options?.onViewEvents?.(category)}
          >
            {getValue() as number}
          </button>
        )

      case 'lastUsed':
        return (
          <div className="text-sm font-medium text-ss-neutral-600 leading-4.5 ">
            {getValue() as string}
          </div>
        )

      case 'status': {
        const status = getValue() as string
        const badgeProps = getStatusBadgeProps(status)
        return (
          <Badge
            variant="outline"
            className={cn(
              'px-2.5 py-1 text-xs font-medium rounded-full',
              badgeProps.className
            )}
          >
            {badgeProps.children}
          </Badge>
        )
      }

      case 'actions':
        return (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-8 w-8 p-0"
                  aria-label="Open menu"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-24">
                <DropdownMenuItem onClick={() => setIsEditCategoryOpen(true)}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <DropdownMenuItem
                          onClick={() => !hasEvents && setDialogOpen(true)}
                          className={cn(
                            hasEvents
                              ? 'text-ss-neutral-400 cursor-not-allowed opacity-50'
                              : 'text-ss-critical-200'
                          )}
                          disabled={hasEvents}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </div>
                    </TooltipTrigger>
                    {hasEvents && (
                      <TooltipContent side="left">
                        <p className="text-xs">
                          This category has {category.events} active{' '}
                          {category.events === 1 ? 'event' : 'events'} and
                          cannot be deleted.
                        </p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </DropdownMenuContent>
            </DropdownMenu>

            <UserActionDialog
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              showForm={false}
              onConfirm={handleDeleteConfirm}
              title="Are you sure you want to delete this category?"
              subtext={`Are you sure you want to delete "${category.name}"?`}
              confirmButtonText="Delete Category"
            />

            {/* Add Category Dialog */}
            <AddCategoryDrawer
              open={isEditCategoryOpen}
              onOpenChange={setIsEditCategoryOpen}
              onSubmit={handleCategorySubmit}
              data={{
                id: category.id,
                name: category.name,
                description: category.description,
                status: category.status,
                events: category.events,
              }}
            />
          </>
        )

      default:
        return getValue() as React.ReactNode
    }
  }
}

export const getCategoryColumns = (
  options?: CategoryColumnsOptions
): ColumnDef<Category>[] => [
  {
    accessorKey: 'name',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Category
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      )
    },
    cell: getCell('name', options),
    enableSorting: true,
  },
  {
    accessorKey: 'description',
    header: 'Description',
    cell: getCell('description', options),
    enableSorting: false,
  },
  {
    accessorKey: 'events',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Events Using
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      )
    },
    cell: getCell('events', options),
    enableSorting: true,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: getCell('status', options),
    enableSorting: true,
  },
  {
    id: 'actions',
    header: '',
    cell: getCell('actions', options),
    enableSorting: false,
  },
]

// Default columns without options for backward compatibility
export const categoryColumns: ColumnDef<Category>[] = getCategoryColumns()
